import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLocation } from "react-router-dom";
import { BOOKING_LINKS } from "../config";
import GrandOpening from "./GrandOpening";

const links = [
  { label: "Services", href: "#services", testid: "nav-services-link" },
  { label: "How It Works", href: "#order", testid: "nav-how-it-works-link" },
  { label: "Pricing", href: "#pricing", testid: "nav-pricing-link" },
  { label: "The App", href: "#app", testid: "nav-app-link" },
  { label: "FAQ", href: "#faq", testid: "nav-faq-link" },
  { label: "Community", href: "/community", testid: "nav-community-link" },
  { label: "Contact", href: "#contact", testid: "nav-contact-link" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const homePrefix = location.pathname === "/" ? "" : "/";
  const items = links.map((l) => ({
    ...l,
    href: l.href.startsWith("#") ? homePrefix + l.href : l.href,
  }));

  return (
    <div className="fixed top-0 z-50 w-full">
      <GrandOpening />
      <header
        data-testid="site-header"
        className="w-full border-b border-[#E2DFD8]/50 bg-[#F9F6F0]/80 backdrop-blur-xl"
      >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-10">
        <a href="#top" data-testid="header-logo" className="group flex items-center gap-4">
          <img
            src="/assets/logo-emblem.png"
            alt="Spintastic City logo"
            className="h-14 w-14 rounded-full object-cover ring-1 ring-[#E2DFD8] transition-transform duration-500 group-hover:rotate-12 sm:h-16 sm:w-16"
          />
          <span className="font-serif text-2xl font-bold tracking-wide sm:text-3xl">
            Spintastic <span className="italic text-[#C85C40]">City</span>
          </span>
        </a>

        <nav className="hidden items-center gap-10 lg:flex" data-testid="desktop-nav">
          {items.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              className="text-sm font-semibold tracking-wide text-[#5C5C5A] transition-colors duration-300 hover:text-[#1A1A1A]"
            >
              {l.label}
            </a>
          ))}
          <a
            href={BOOKING_LINKS.pickupDelivery}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-order-now-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] px-6 py-3 text-sm font-bold text-[#F9F6F0] transition-[background-color,transform] duration-300 hover:scale-[0.98] hover:bg-[#C85C40]"
          >
            Order Now
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E2DFD8] lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-[#E2DFD8]/50 bg-[#F9F6F0]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {items.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => setOpen(false)}
                  className="rounded-2xl px-4 py-3 font-serif text-2xl tracking-tight transition-colors duration-300 hover:bg-[#EFEAE1]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={BOOKING_LINKS.pickupDelivery}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-order-now-btn"
                onClick={() => setOpen(false)}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#C85C40] px-6 py-4 text-sm font-bold text-white"
              >
                Order Now <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
      </header>
    </div>
  );
}
